import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
// @ts-expect-error — diff-match-patch has no bundled types
import DiffMatchPatch from 'diff-match-patch';

interface Props {
  originalText: string;
  proposedText: string;
  citationCount: number;
  onAccept: (finalText: string) => void;
  onReject: () => void;
  onRefine: (instruction: string) => void;
  refining: boolean;
}

type DiffOp = [-1 | 0 | 1, string];

function computeDiff(original: string, proposed: string): DiffOp[] {
  const dmp = new DiffMatchPatch();
  const diffs: DiffOp[] = dmp.diff_main(original, proposed);
  dmp.diff_cleanupSemantic(diffs);
  return diffs;
}

/** Render diffs as inline spans — deleted text struck through, inserted underlined. */
const DiffView: React.FC<{ diffs: DiffOp[] }> = ({ diffs }) => (
  <span>
    {diffs.map(([op, text], i) => {
      if (op === 0) return <span key={i}>{text}</span>;
      if (op === -1) return <span key={i} className="line-through text-red-500 bg-red-50">{text}</span>;
      return <span key={i} className="underline text-green-700 bg-green-50">{text}</span>;
    })}
  </span>
);

const FieldUpdateDiffReview: React.FC<Props> = ({
  originalText, proposedText, citationCount, onAccept, onReject, onRefine, refining,
}) => {
  const { t } = useTranslation();
  const [refinementText, setRefinementText] = useState('');
  const [showRefinement, setShowRefinement] = useState(false);

  const diffs = useMemo(() => computeDiff(originalText, proposedText), [originalText, proposedText]);

  const handleRefine = () => {
    if (!refinementText.trim()) return;
    onRefine(refinementText.trim());
    setRefinementText('');
    setShowRefinement(false);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-700">
          {t('fieldUpdate.diffTitle', 'Review proposed changes')}
        </h3>
        {citationCount > 0 && (
          <span className="text-xs text-slate-400">
            {t('fieldUpdate.citationCount', '{{n}} citations', { n: citationCount })}
          </span>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3 text-xs">
        <div>
          <p className="font-medium text-slate-500 mb-1">{t('fieldUpdate.currentField', 'Current')}</p>
          <div className="border border-slate-200 rounded p-3 bg-slate-50 whitespace-pre-wrap min-h-24 max-h-[60vh] overflow-y-auto text-slate-600">
            {originalText || <span className="text-slate-300 italic">{t('fieldUpdate.empty', '(empty)')}</span>}
          </div>
        </div>
        <div>
          <p className="font-medium text-slate-500 mb-1">{t('fieldUpdate.proposedField', 'Proposed')}</p>
          <div className="border border-blue-200 rounded p-3 bg-blue-50 whitespace-pre-wrap min-h-24 max-h-[60vh] overflow-y-auto text-slate-700">
            <DiffView diffs={diffs} />
          </div>
        </div>
      </div>

      {showRefinement && (
        <div className="space-y-2">
          <label className="text-xs font-medium text-slate-600">
            {t('fieldUpdate.refinementInstruction', 'Refinement instruction')}
          </label>
          <textarea
            rows={2}
            value={refinementText}
            onChange={e => setRefinementText(e.target.value)}
            placeholder={t('fieldUpdate.refinementPlaceholder', 'e.g. Focus more on thermal aspects, remove cost discussion...')}
            className="w-full border border-slate-300 rounded px-2 py-1.5 text-xs resize-none"
          />
        </div>
      )}

      <div className="flex items-center justify-between pt-1">
        <div className="flex gap-2">
          {!showRefinement && (
            <button
              onClick={() => setShowRefinement(true)}
              className="px-3 py-1.5 text-xs text-slate-600 border border-slate-300 rounded hover:bg-slate-50"
            >
              {t('fieldUpdate.refineBtn', 'Refine...')}
            </button>
          )}
          {showRefinement && (
            <>
              <button
                onClick={handleRefine}
                disabled={refining || !refinementText.trim()}
                className="px-3 py-1.5 text-xs bg-slate-700 text-white rounded hover:bg-slate-800 disabled:opacity-50"
              >
                {refining ? t('fieldUpdate.refining', 'Refining...') : t('fieldUpdate.applyRefinement', 'Apply')}
              </button>
              <button
                onClick={() => setShowRefinement(false)}
                className="px-3 py-1.5 text-xs text-slate-500 hover:text-slate-700"
              >
                {t('common.cancel', 'Cancel')}
              </button>
            </>
          )}
        </div>

        <div className="flex gap-2">
          <button
            onClick={onReject}
            className="px-3 py-1.5 text-xs text-slate-600 border border-slate-300 rounded hover:bg-slate-50"
          >
            {t('fieldUpdate.rejectBtn', 'Reject')}
          </button>
          <button
            onClick={() => onAccept(proposedText)}
            className="px-4 py-1.5 text-xs bg-green-600 text-white rounded hover:bg-green-700"
          >
            {t('fieldUpdate.acceptBtn', 'Accept')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FieldUpdateDiffReview;
